import { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { TourxLogo } from './components/TourxLogo';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { TachoPage } from './pages/TachoPage';
import { UsersPage } from './pages/UsersPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { WalkaroundPage } from './pages/WalkaroundPage';
import { FleetPage } from './pages/FleetPage';
import { CalendarPage } from './pages/CalendarPage';
import { CompliancePage } from './pages/CompliancePage';
import { ReportsPage } from './pages/ReportsPage';
import { IntelligencePage } from './pages/IntelligencePage';
import { DepotsPage } from './pages/DepotsPage';
import { DriversPage } from './pages/DriversPage';
import { QuotePage } from './pages/QuotePage';
import { JobsPage } from './pages/JobsPage';
import { WorkshopPage } from './pages/WorkshopPage';
import { OpsPage } from './pages/OpsPage';
import { JobSheetsPage } from './pages/JobSheetsPage';
import { AccountsPage } from './pages/AccountsPage';
import TrackingPage from './pages/TrackingPage';
import { VUDownloadsPage } from './pages/VUDownloadsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SupportPage } from './pages/SupportPage';
import { MarketingPage } from './pages/MarketingPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';

function Router() {
  const { user, loading } = useAuth();
  const [path, setPath] = useState(window.location.pathname || '/');

  useEffect(() => {
    // Listen for popstate (back/forward)
    const onPop = () => setPath(window.location.pathname || '/');
    window.addEventListener('popstate', onPop);

    // Intercept all <a> clicks for SPA navigation
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#')) return;
      e.preventDefault();
      window.history.pushState({}, '', href);
      setPath(href);
    };
    document.addEventListener('click', onClick);

    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('click', onClick);
    };
  }, []);

  // ── Public routes (no login required) ────────────────────────────
  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/support') return <SupportPage />;
  if (path === '/marketing') return <MarketingPage />;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center animate-pulse">
          <div className="flex justify-center mb-3">
            <TourxLogo height={48} />
          </div>
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in — show login page
  if (!user) {
    return <LoginPage />;
  }

  // Force password change on first login
  if ((user as any).must_change_password) {
    return <ChangePasswordPage />;
  }

  // Module access check — null/undefined means full access
  const userModules = (user as any)?.allowed_modules as string[] | null | undefined;

  // Map path → module key
  const PATH_TO_MODULE: Record<string, string> = {
    '/': 'dashboard', '/drivers': 'drivers', '/tacho': 'tacho',
    '/calendar': 'calendar', '/compliance': 'compliance', '/reports': 'reports',
    '/users': 'users', '/intelligence': 'intelligence', '/depots': 'depots',
    '/fleet': 'fleet', '/quote': 'quote', '/jobs': 'jobs',
    '/walkaround': 'walkaround', '/workshop': 'workshop', '/ops': 'ops',
    '/accounts': 'accounts', '/tracking': 'tracking', '/jobsheets': 'jobsheets',
    '/vu-downloads': 'vu-downloads',
  };

  const moduleKey = PATH_TO_MODULE[path];
  const hasModuleAccess = !userModules || !moduleKey || userModules.includes(moduleKey);

  // If user doesn't have access to this module, redirect to the first allowed module
  if (!hasModuleAccess && path !== '/login') {
    const firstAllowed = userModules?.find(m => Object.values(PATH_TO_MODULE).includes(m));
    const fallbackPath = firstAllowed
      ? Object.entries(PATH_TO_MODULE).find(([, v]) => v === firstAllowed)?.[0] || '/'
      : '/';
    // Navigate to the first allowed page
    window.history.replaceState({}, '', fallbackPath);
    // Re-render with the correct path
    return <HomePage />;
  }

  // Logged in — route to the correct page
  switch (path) {
    case '/':
      return <HomePage />;
    case '/drivers':
      return <DriversPage />;
    case '/tacho':
      return <TachoPage />;
    case '/calendar':
      return <CalendarPage />;
    case '/compliance':
      return <CompliancePage />;
    case '/reports':
      return <ReportsPage />;
    case '/users':
      return <UsersPage />;
    case '/intelligence':
      return <IntelligencePage />;
    case '/depots':
      return <DepotsPage />;
    case '/fleet':
      return <FleetPage />;
    case '/quote':
      return <QuotePage />;
    case '/jobs':
      return <JobsPage />;
    case '/walkaround':
      return <WalkaroundPage />;
    case '/workshop':
      return <WorkshopPage />;
    case '/ops':
      return <OpsPage />;
    case '/jobsheets':
      return <JobSheetsPage />;
    case '/accounts':
      return <AccountsPage />;
    case '/tracking':
      return <TrackingPage />;
    case '/vu-downloads':
      return <VUDownloadsPage />;
    case '/login':
      return <LoginPage />;
    default:
      return <HomePage />;
  }
}

export default function App() {
  return <Router />;
}
